import { CARD_BORDER, CHROME_GREY, TEXT_META, WHITE } from "../tokens";
import { FiSlash } from "react-icons/fi";
import styled from "styled-components";
// @ts-ignore
import scaleSvg from "../assets/energy-certificate-scale.svg";

// A–G energy efficiency scale (CEE), shared by user-interface and admin
// BlockRenderers. The 7-row scale graphic is the official MITECO/IDAE artwork
// (copied verbatim from edificioseficientes.gob.es as
// assets/energy-certificate-scale.svg — same image Catalunya's ICAEN visor
// reuses) so the shape/colors match what buyers already recognize from the
// real certificate. We paint two floating markers on top of it — consumo
// (primary energy) and emisiones (CO2) — each pointing at its own row via a
// left-pointing triangle, exactly like the source site's overlay.
export interface EnergyCertificateScaleProps {
  consumptionLetter: string | null;
  consumptionValue: number | null;
  emissionsLetter: string | null;
  emissionsValue: number | null;
  labels?: { unavailable?: string; unavailableCaption?: string; scaleAlt?: string };
}

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"] as const;
type Letter = (typeof LETTERS)[number];

// Vertical center (top %, within the scale image) of each row — measured
// directly from assets/energy-certificate-scale.svg's own geometry (each
// letter glyph's bounding box within the 1325-tall viewBox), then verified
// pixel-for-pixel against a rendered screenshot. Evenly spaced ~12.6 points
// apart, first row centered at 15.6%. Must keep matching the source image if
// that's ever swapped for a newer export.
const ROW_TOP_PERCENT: Record<Letter, number> = {
  A: 15.6,
  B: 28.2,
  C: 40.8,
  D: 53.4,
  E: 66.0,
  F: 78.6,
  G: 91.2,
};

// Horizontal center (left %, within the scale image) of the "Consumo" and
// "Emisiones" columns — measured the same way as ROW_TOP_PERCENT, from a
// rendered screenshot's column boundaries (68.3 / 87.9), then nudged right by
// ~1.15 points (half of the marker's 0.6rem triangle, as a % of the 26rem
// container) so the whole marker shape — box plus its left-pointing triangle,
// not just the number box — reads as centered in the column.
const CONSUMPTION_LEFT_PERCENT = 69.5;
const EMISSIONS_LEFT_PERCENT = 89.1;

const normalizeLetter = (letter: string | null): Letter | null => {
  if (!letter) return null;
  const upper = letter.trim().toUpperCase();
  return (LETTERS as readonly string[]).includes(upper) ? (upper as Letter) : null;
};

// Fills its container on narrow screens (width:100%) but stops growing past
// a comfortable reading size on wide ones (max-width) instead of scaling up
// with the page — and stays centered either way.
const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 26rem;
  margin: 0.75rem auto;
`;

const ScaleImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const Marker = styled.span<{ $top: number; $left: number }>`
  position: absolute;
  top: ${(p) => p.$top}%;
  left: ${(p) => p.$left}%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 1.6rem;
  width: 4rem;
  padding: 0 0.4rem;
  background: #141414;
  color: ${WHITE};
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 0 0.15rem 0.15rem 0;
  white-space: nowrap;

  &::before {
    content: "";
    position: absolute;
    left: -0.6rem;
    top: 0;
    border-top: 0.8rem solid transparent;
    border-bottom: 0.8rem solid transparent;
    border-right: 0.6rem solid #141414;
  }
`;

// With no rating to point at, the scale is shown greyed out
const GreyedScale = styled.div`
  filter: grayscale(1);
  opacity: 0.5;
`;

const UnavailablePill = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: ${WHITE};
  border: 1px solid ${CARD_BORDER};
  border-radius: 100px;
  box-shadow: 0 4px 16px rgba(20, 50, 59, 0.1);
  color: ${CHROME_GREY};
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
`;

const UnavailableCaption = styled.p`
  margin: 0.5rem auto 0;
  max-width: 26rem;
  font-size: 0.7rem;
  line-height: 1.5;
  color: ${TEXT_META};
`;

export const EnergyCertificateScale = ({
  consumptionLetter,
  consumptionValue,
  emissionsLetter,
  emissionsValue,
  labels,
}: EnergyCertificateScaleProps) => {
  const cLetter = normalizeLetter(consumptionLetter);
  const eLetter = normalizeLetter(emissionsLetter);
  const unavailableLabel = labels?.unavailable ?? "Sin certificado energético";

  if (!cLetter && !eLetter) {
    const caption = labels?.unavailableCaption;
    return (
      <>
        <Container role="img" aria-label={`Calificación energética — ${unavailableLabel}`}>
          <GreyedScale>
            <ScaleImage
              src={scaleSvg}
              alt={labels?.scaleAlt ?? "Escala de calificación energética A-G"}
            />
          </GreyedScale>
          <UnavailablePill>
            <FiSlash size={13} aria-hidden />
            {unavailableLabel}
          </UnavailablePill>
        </Container>
        {caption && <UnavailableCaption>{caption}</UnavailableCaption>}
      </>
    );
  }

  const ariaParts = [
    cLetter && `consumo ${cLetter}${consumptionValue != null ? `, ${consumptionValue} kWh/m²·año` : ""}`,
    eLetter && `emisiones ${eLetter}${emissionsValue != null ? `, ${emissionsValue} kg CO₂/m²·año` : ""}`,
  ].filter(Boolean);

  return (
    <Container role="img" aria-label={`Calificación energética — ${ariaParts.join(", ")}`}>
      <ScaleImage src={scaleSvg} alt={labels?.scaleAlt ?? "Escala de calificación energética A-G"} />
      {cLetter && (
        <Marker $top={ROW_TOP_PERCENT[cLetter]} $left={CONSUMPTION_LEFT_PERCENT}>
          {consumptionValue != null ? consumptionValue : cLetter}
        </Marker>
      )}
      {eLetter && (
        <Marker $top={ROW_TOP_PERCENT[eLetter]} $left={EMISSIONS_LEFT_PERCENT}>
          {emissionsValue != null ? emissionsValue : eLetter}
        </Marker>
      )}
    </Container>
  );
};
